import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Users, TrendingUp } from 'lucide-react';
import CustomerTable from '../../components/Customers/CustomerTable';
import CustomerFilters from '../../components/Customers/CustomerFilters';
import CustomerPagination from '../../components/Customers/CustomerPagination';
import CustomerForm from '../../components/Customers/CustomerForm';
import {
  fetchCustomers,
  createCustomer,
  deleteCustomer,
  fetchCustomerStats,
} from '../../services/customerApi';

const Customers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [mode, setMode] = useState(searchParams.get('mode') || null);
  const [customerId, setCustomerId] = useState(searchParams.get('id') || null);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    sortBy: searchParams.get('sortBy') || 'firstName',
    sortOrder: searchParams.get('sortOrder') || 'asc',
  });

  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: parseInt(searchParams.get('limit')) || 10,
    total: 0,
    totalPages: 0,
  });

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      // Add filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });

      // Add pagination to params
      params.set('page', pagination.page.toString());
      params.set('limit', pagination.limit.toString());

      const response = await fetchCustomers(params);
      setCustomers(response.customers);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  const loadStats = useCallback(async () => {
    try {
      const statsData = await fetchCustomerStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadStats();
  }, [loadCustomers, loadStats]);

  // Update URL when filters or pagination change
  useEffect(() => {
    const params = new URLSearchParams();

    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    // Add pagination
    params.set('page', pagination.page.toString());
    params.set('limit', pagination.limit.toString());

    // Add mode and id if in form mode
    if (mode) {
      params.set('mode', mode);
      if (customerId) params.set('id', customerId);
    }

    setSearchParams(params);
  }, [filters, pagination, mode, customerId, setSearchParams]);

  const handleCreateCustomer = async (customerData) => {
    try {
      await createCustomer(customerData);
      await loadCustomers();
      await loadStats();
      handleBackToList();
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await deleteCustomer(id);
        await loadCustomers();
        await loadStats();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const handleViewCustomer = (customer) => {
    setMode('view');
    setCustomerId(customer.id);
  };

  const handleNewCustomer = () => {
    setMode('add');
    setCustomerId(null);
  };

  const handleBackToList = () => {
    setMode(null);
    setCustomerId(null);
  };

  const handleSort = (field) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Render form mode (add/view)
  if (mode) {
    return (
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === 'add' && 'Add New Customer'}
              {mode === 'view' && 'View Customer'}
            </h1>
            <p className="text-gray-600">
              {mode === 'add' && 'Create a new customer in your system'}
              {mode === 'view' && 'View customer details'}
            </p>
          </div>
          <button
            onClick={handleBackToList}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            ← Back to Customers
          </button>
        </div>

        {/* Customer Form */}
        <CustomerForm
          customer={mode === 'add' ? null : { id: customerId }}
          mode={mode}
          onSubmit={mode === 'add' ? handleCreateCustomer : null}
          onCancel={handleBackToList}
        />
      </div>
    );
  }

  // Render list mode
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Customers Management
          </h1>
          <p className="text-gray-600">
            Manage your customer database and relationships
            {pagination && (
              <span className="ml-2 text-sm text-gray-500">
                ({pagination.total} customers)
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleNewCustomer}
          className="px-4 py-2 bg-blue-600 flex items-center justify-center text-white rounded-lg hover:bg-blue-700 transition-colors "
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Customer
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Total Customers
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.totalCustomers}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  New This Month
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.newThisMonth || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <CustomerFilters filters={filters} onFiltersChange={setFilters} />

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <CustomerTable
          customers={customers}
          loading={loading}
          onView={handleViewCustomer}
          onDelete={handleDeleteCustomer}
          onSort={handleSort}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
        />
      </div>

      {/* Pagination */}
      <div className="mt-6">
        <CustomerPagination
          pagination={pagination}
          loading={loading}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          onPageSizeChange={(limit) =>
            setPagination((prev) => ({ ...prev, limit, page: 1 }))
          }
        />
      </div>
    </div>
  );
};

export default Customers;
